import { Component, OnInit, ChangeDetectorRef, OnDestroy, ViewChild, Input, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { GridOptions, GridReadyEvent, ValueGetterParams } from 'ag-grid-community';
import { ColDef } from 'ag-grid-enterprise';
import { DropdownService } from '../../services/dropdown.service';
import { OpenDeleteCellComponent } from '../../../ag-grid/components/renderers/open-delete-cell/open-delete-cell.component';
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { Disclosure } from '../../models/disclosures/disclosure.model';
import { MainService } from '../../ui/services/main.service';
import { DisclosureService } from '../../services/disclosure.service';
import { LoggingService } from '../../services/logging.service';
import { OpenRollbackCellComponent } from '../../../ag-grid/components/renderers/open-rollback-cell/open-rollback-cell.component';
import { AmendCellComponent } from '../../../ag-grid/components/renderers/amend-cell/amend-cell.component';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { first } from 'rxjs';
import { FileService } from '../../services/file.service';
import { DownloadXmlCellComponent } from '../../../ag-grid/components/renderers/download-xml-cell/download-xml-cell.component';
import { AGGridHelperService } from '../../../ag-grid/services/ag-grid-helper-service';
import { FFUserWithRoles } from '../../models/users/ff-user-with-roles.model';
import { ColdObservable } from 'rxjs/internal/testing/ColdObservable';
import { DateCellRendererComponent } from '../../../ag-grid/components/renderers/date-cell-renderer/date-cell-renderer.component';
import { IDateCellRendererParams } from '../../../ag-grid/models/date-cell-renderer-params.model';
import { UserService } from '../../services/user.service';
import { LU_Status } from '../../models/enums/lu-status.enum';
import { OrganizationRelation } from '../../models/organizations/organization-relation.model';

const FileSaver = require('file-saver');
@Component({
  selector: 'disclosure-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './disclosure-list.component.html',
  styleUrls: ['./disclosure-list.component.scss']
})
export class DisclosureListComponent implements OnInit, OnDestroy {
  public wellApiNum: string = "";
  public wellName: string = "";
  public states: any[];
  public counties: any[];
  public countyOptions:any[] = [];
  public pending: Disclosure[] = [];
  public submitted: Disclosure[] = [];
  public amending: Disclosure[] = [];
  public deleted: Disclosure[] = [];
  public all: Disclosure[] = [];
  public deletedByOperator: any[] = [];
  public totalPending: number = 0;
  public totalSubmitted: number = 0;
  public totalAmending: number = 0;
  public totalDeleted: number = 0;
  public totalDeletedByOperator: number = 0;
  public totalAll: number = 0;
  public disclosuresFiltered: any[] = [];
  public modalVisible = false;
  public modalAmendVisible = false;
  public modalRollbkVisible = false
  private isReadOnly = false;
  public isServiceComp = false;
  private primaryOrganizationId: string;
  private currentOrganizationId: string;
  public selectedDisclosure: Disclosure = null;
  public userInfo: FFUserWithRoles;
  public gridOptions: GridOptions;
  public gridHeight: string = "";
  orgRelations: OrganizationRelation[] = [];
  public gridHelpText: string = "";

  @Input() public parentComponentName: string = "";
  @Input() public disclosures: any[] = [];
  public filterForm: UntypedFormGroup = new UntypedFormGroup({
    status: new UntypedFormControl("Editing")
  });
  public readonly columnDefs: ColDef[] = [
    /* Default */
    { 
      field: 'apiNumber', 
      headerName: 'API Number',
      filter: "agTextColumnFilter",
      cellClass: 'stringType',
      editable: false,
      width: 110,
      valueGetter: this.apiValueGetter.bind(this),
      suppressColumnsToolPanel: true,
      lockVisible: true
    },
    {
      colId: 'operatorName',
      field: 'operatorName', 
      headerName: 'Operator Name',
      filter: "agTextColumnFilter",
      editable: false,
      hide: false,
      width: 120,
      cellRenderer: (data: any) => {
        if (data?.data) {
          const disclosure = data.data as Disclosure
          return disclosure.operatorName
        }

        return ''
      }
    },
    {
      headerName: 'Well Name and Number',
      field: 'wellName',
      filter: "agTextColumnFilter",
      resizable: true,
      width: 110
    },
    {
      field: 'jobStartDate',
      headerName: 'Job Start Date',
      filter: 'agDateColumnFilter',
      filterParams: {
        buttons: ['reset'],
        dateFormat: 'mm/dd/yyyy',
        comparator: this.agGridHelperService.dateFilterComparer
      },
      headerTooltip:'Start date filter of the range of jobs performed',
      width: 100,
      editable: false,
      cellRenderer: DateCellRendererComponent
    },
    {
      field: 'jobEndDate',
      headerName: 'Job End Date',
      filter: 'agDateColumnFilter',
      filterParams: {
        buttons: ['reset'],
        dateFormat: 'mm/dd/yyyy',
        comparator: this.agGridHelperService.dateFilterComparer
      },
      headerTooltip:'End date filter of the range of jobs performed',
      editable: false,
      width: 120,
      cellRenderer: DateCellRendererComponent
    },
    {
      field: 'lastModifiedDate',
      headerName: 'Last Edit',
      filter: 'agDateColumnFilter',
      filterParams: {
        buttons: ['reset'],
        dateFormat: 'mm/dd/yyyy',
        comparator: this.agGridHelperService.dateFilterComparer
      },
      editable: false,
      width: 100,
      cellRenderer: DateCellRendererComponent
    },
    /* Optional */
    {
      field: 'longitude',
      headerName: 'Longitude',
      filter: "agNumberColumnFilter",
      editable: false,
      hide: true,
      width: 100,
      cellRenderer: (data: any) => {
        if(data?.data){
          const disclosure = data.data as Disclosure
          return disclosure.longitude
        }

        return ''
      },
    },
    {
      field: 'latitude',
      headerName: 'Latitude',
      filter: "agNumberColumnFilter",
      editable: false,
      hide: true,
      width: 100,
      cellRenderer: (data: any) => {
        if(data?.data){
          const disclosure = data.data as Disclosure
          return disclosure.latitude
        }
        
        return ''
      },
    },
    {
      field: 'projection',
      headerName: 'Datum',
      filter: "agSetColumnFilter",
      editable: false,
      hide: true,
      width: 100,
      cellRenderer: (data: any) => {
        if(data?.data){
          const disclosure = data.data as Disclosure
          return disclosure.projection
        }

        return ''
      },
    },
    {
      field: 'tvd',
      headerName: 'TVD',
      filter:'agNumberColumnFilter',
      editable: false,
      hide: true,
      width: 100,
      cellRenderer: (data: any) => {
        if(data?.data){
          const disclosure = data.data as Disclosure
          return disclosure.tvd
        }

        return ''
      },
    },
    {
      field: 'isFederalWell',
      headerName: 'Federal Well',
      filter: 'agSetColumnFilter',
      editable: false,
      hide: true,
      width: 100,
      cellRenderer: (data: any) => {
        if(data?.data){
          const disclosure = data.data as Disclosure
          return disclosure.isFederalWell
        }

        return ''
      },
    },
    {
      field: 'isIndianWell',
      headerName: 'Indian Well',
      filter: 'agSetColumnFilter',
      editable: false,
      hide: true,
      width: 100,
      cellRenderer: (data: any) => {
        if(data?.data){
          const disclosure = data.data as Disclosure
          return disclosure.isIndianWell
        }

        return ''
      },
    },
    {
      field: 'lastModifiedBy',
      headerName: 'Last Editor',
      filter:'agTextColumnFilter',
      editable: false,
      hide: true,
      width: 100,
      cellRenderer: (data: any) => {
        if(data?.data){
          const disclosure = data.data as Disclosure
          return disclosure.lastModifiedBy
        }

        return ''
      },
    },
    {
      field: 'totalBaseWaterVolume',
      headerName: 'Total Water',
      filter:'agNumberColumnFilter',
      editable: false,
      hide: true,
      width: 100,
      cellRenderer: (data: any) => {
        if(data?.data){
          const disclosure = data.data as Disclosure
          return disclosure.totalBaseWaterVolume
        }

        return ''
      },
    },
    {
      field: 'entryMethod',
      headerName: 'Entry Method',
      filter: 'agSetColumnFilter',
      editable: false,
      hide: true,
      width: 100,
      cellRenderer: (data: any) => {
        if(data?.data){
          const disclosure = data.data as Disclosure
          return disclosure.entryMethod
        }

        return ''
      },
    },
    {
      field: 'createdDate',
      headerName: 'Create Date',
      editable: false,
      hide: true,
      width: 100,
      filter: 'agDateColumnFilter',
      filterParams: {
        buttons: ['reset'],
        dateFormat: 'mm/dd/yyyy',
        comparator: this.agGridHelperService.dateFilterComparer
      },
      cellRenderer: DateCellRendererComponent
    },
    {
      field: 'serviceCompanyName',
      colId: 'serviceCompanyName',
      headerName: 'Service Company Name',
      filter:'agTextColumnFilter',
      editable: false,
      hide: true,
      width: 120,
      cellRenderer: (data: any) => {
        if(data?.data){
          const disclosure = data.data as Disclosure
          return disclosure.serviceCompanyName
        }
        
        return '' 
      }
    },   
  ];
  
  public filteredColDefs: ColDef[] = []

  public editMode = false;

  public buildGridOptions() {

    this.gridOptions = {
      defaultColDef: {
        resizable: true,
        sortable: true,
      },
      excelStyles:this.agGridHelperService.getDefaultExcelStyles(),
      pagination: true,
      editType: 'fullRow',
      accentedSort: true,
      rowHeight: 40,
      suppressContextMenu: true,
      suppressClickEdit: true,
      suppressMenuHide: true,
      rowClassRules: {
        'disabled': (params: any) => {
          return this.editMode === true && params.data.disabled === true
        }
      }
    } as GridOptions;
  }

  public frameworkComponents: any = {
    OpenDeleteCellComponent: OpenDeleteCellComponent,
    OpenRollbackCellComponent: OpenRollbackCellComponent,
    OpenCellComponent: AmendCellComponent,
    DownloadXmlCellComponent: DownloadXmlCellComponent
  };

  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;
  public gridApi: any;
  possibleOrgsToAdd: any;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private dropdownService: DropdownService,
    private mainService: MainService,
    private disclosureService: DisclosureService,
    private loggingService: LoggingService,
    private router: Router,
    private authService: AuthService,
    private fileService: FileService,
    private agGridHelperService: AGGridHelperService,
    private userService: UserService
  ) {
  }

  ngOnInit(): void {
    this.gridHelpText = this.agGridHelperService.gridInfoHelpText;
    this.buildGridOptions();
    this.authService.getUserInfo()
    .then(user => {
      this.userInfo = user;
      if (user.currentOrganizationId !== null && user.currentOrganizationId !== undefined) {
        this.mainService.showLoading({ loading: true });
      }
      this.currentOrganizationId = user.currentOrganizationId;
      this.isReadOnly = !user.hasOrgRole('DataSubmitter') && !user.isAdmin;
      let currentOrg = user.getCurrentOrg();
      this.isServiceComp = (currentOrg) ? currentOrg.isServiceComp : false;
      if (currentOrg.isRegisteredAgent) {
        this.userService.getMyCompany(currentOrg.organizationId).subscribe(res => {
          this.orgRelations = res.organizationRelations.filter(x => x.organization.isOperator && x.statusId === LU_Status.Active);
          this.mainService.showLoading({ loading: false });
        }, err => {
          this.loggingService.error("Error retrieving organization information");
          this.mainService.showLoading({ loading: false });
        });
      }
      this.filteredColDefs = this.getColumnDefs("Editing");
      this.changeDetectorRef.detectChanges(); 
    });
    
    this.dropdownService.getCounties().then(ctys => this.counties = ctys);
    this.dropdownService.getStates().then(states => this.states = states);

  }

  public onGridReady(params: GridReadyEvent) {

    this.gridApi = params.api;
    this.gridApi.onActionTrigger = (event: any) => {
      this.triggerAction(event);
    };
    
    this.gridApi.setColumnDefs(this.filteredColDefs);
    this.gridApi.sizeColumnsToFit();

    window.onresize = () => {
      this.gridApi.sizeColumnsToFit();
    }
  }

  private triggerAction(event: any) {
    switch(event.action){
      case "delete":
        this.modalVisible = true;
        this.selectedDisclosure = event.data as Disclosure;
        break;

      case "startAmend":
        this.modalAmendVisible = true;
        this.selectedDisclosure = event.data as Disclosure;
        break;

      case "rollback":
        this.modalRollbkVisible = true;
        this.selectedDisclosure = event.data as Disclosure;
        break;

      case "viewPdf":
        this.viewPdf(event.data);
        break;

      case "viewXml":
        this.viewXml(event.data);
        break;
    }
  }

  statusFormatter(params: any) {
    //if (params.value === "Editing") return "Pending";
    return params.value;
  }

  public formUpdated($event: any) {
    const value = $event.target.value

    switch (value) {
      case "Editing":
        this.disclosuresFiltered = this.pending;
        this.filteredColDefs = this.getColumnDefs(value);
        break;

      case "Submitted":
        this.disclosuresFiltered = this.submitted;
        this.filteredColDefs = this.getColumnDefs(value);
        break;
      
      case "Amending":
        this.disclosuresFiltered = this.amending;
        this.filteredColDefs = this.getColumnDefs(value);
        break;

      case "Deleted":
        this.disclosuresFiltered = this.deleted;
        this.filteredColDefs = this.getColumnDefs(value);
        break;
      
      case "Deleted by Operator":
        this.disclosuresFiltered = this.deletedByOperator;
        this.filteredColDefs = this.getColumnDefs(value);
        break;

      default:
        this.disclosuresFiltered = this.all;
        this.filteredColDefs = this.getColumnDefs("All");
    }

    this.changeDetectorRef.detectChanges(); //Called because grid was not resizing properly with modified filteredColDefs
    this.gridApi.setColumnDefs(this.filteredColDefs);
    this.gridApi.sizeColumnsToFit();
  }

  private getColumnDefs(view: "Editing" | "Submitted" | "Amending" | "Deleted" | "All" | "Deleted by Operator"): ColDef[]{
    const primaryOrg = this.userInfo.getCurrentOrg();
    let isServiceComp = false;
    let isOperator = false;
    let isRegisteredAgent = false;
    if (primaryOrg) {
      isServiceComp = primaryOrg.isServiceComp;
      isOperator = primaryOrg.isOperator;
      isRegisteredAgent = primaryOrg.isRegisteredAgent;
    }

    let defs: ColDef[] = [...this.columnDefs];

    let amendDateCol = {
      field: 'submitTimeStamp',
      colId: 'amendDate',
      headerName: 'Amend Date',
      editable: false,
      hide: true,
      width: 120,
      filter: 'agDateColumnFilter',
      filterParams: {
        buttons: ['reset'],
        dateFormat: 'mm/dd/yyyy',
        comparator: this.agGridHelperService.dateFilterComparer
      },
      cellRenderer: DateCellRendererComponent,
    } as ColDef;

    let amendReasonCol = {
      field: 'amendedReason',
      colId: 'amendReason',
      headerName: 'Amend Reason',
      filter:'agTextColumnFilter',
      editable: false,
      hide: true,
      width: 120,
      cellRenderer: (data: any) => {
        if (data?.data) {
          const disclosure = data.data as Disclosure
          return disclosure.amendedReason
        }

        return ''
      }
    } as ColDef;
    
    let publicStatus = {
      field: 'publicStatus',
      colId: 'publicStatus',
      headerName: 'Status',
      filter:'agTextColumnFilter',
      editable: false,
      hide: true,
      width: 120,
    } as ColDef;

    let ffVersionCol = {
      field: 'ffVersion',
      colId: 'ffVersion',
      headerName: 'FFVersion',
      filter:'agNumberColumnFilter',
      editable: false,
      hide: true,
      width: 120,
      cellRenderer: (data: any) => {
        if (data?.data) {
          const disclosure = data.data as Disclosure
          return disclosure.ffVersion
        }

        return ''
      }
    } as ColDef;

    let submitDate = {
      field: 'submitTimeStamp',
      colId: 'submitTimeStamp',
      headerName: 'Submit Date',
      editable: false,
      hide: false,
      width: 120,
      filter: 'agDateColumnFilter',
      filterParams: {
        buttons: ['reset'],
        dateFormat: 'mm/dd/yyyy',
        comparator: this.agGridHelperService.dateFilterComparer
      },
      cellRenderer: DateCellRendererComponent,
    }

    let statusCol = {
      width: 90,
      field: 'publicStatus',
      headerName: "Status",
      filter: 'agSetColumnFilter',
      valueFormatter: this.statusFormatter,
    } as ColDef;

    let routedByCol = {
      field: 'routedBy',
      colId: 'routedBy',
      headerName: 'Routed By',
      filter:'agTextColumnFilter',
      editable: false,
      hide: false,
      width: 120,
      cellRenderer: (data: any) => {
        if (data?.data) {
          const disclosure: Disclosure = data.data;
          return disclosure.routedBy;
        }

        return '';
      }
    } as ColDef;

    let uploadedDirectly = {
      field: 'uploadedDirectly',
      colId: 'uploadedDirectly',
      headerName: 'Direct Upload',
      filter: 'agSetColumnFilter',
      editable: false,
      hide: false,
      width: 120,
      cellRenderer: (data: any) => {
        if (data?.data) {
          const disclosure: Disclosure = data.data;
          return disclosure.uploadedDirectly ? 'Yes' : 'No';
        }

        return '';
      }
    } as ColDef;

    let origSubmitDate = {
      field: 'originalSubmitTimeStamp',
      colId: 'originalSubmitTimeStamp',
      headerName: 'Original Submit Date',
      editable: false,
      hide: true,
      width: 100,
      filter: 'agDateColumnFilter',
      filterParams: {
        buttons: ['reset'],
        dateFormat: 'mm/dd/yyyy',
        comparator: this.agGridHelperService.dateFilterComparer
      },
      cellRenderer: DateCellRendererComponent,
    } as ColDef;

    let submittedByCol = {
      field: 'submittedBy',
      colId: 'submitUser',
      headerName: 'Submitted By',
      filter:'agTextColumnFilter',
      editable: false,
      hide: false,
      width: 120,
      cellRenderer: (data: any) => {
        if (data?.data && data.data.submittedBy) {
          const disclosure: Disclosure = data.data;
          return disclosure.submittedBy;
        }

        return ''
      }
    } as ColDef;

    switch(view){
      case "Editing":
        if(!this.isReadOnly){
          defs.push({
            colId: 'actions',
            cellRenderer: OpenDeleteCellComponent,
            width: 90,
            hide: false
          })
        }
        if(!this.isReadOnly && isServiceComp){
          defs.push(
            {
              colId: 'actions',
              cellRenderer: DownloadXmlCellComponent,
              width: 50,
              hide: false
            }
          );
        }
        break;

      case "Submitted":
        if(isServiceComp){
          defs.push(
            statusCol,
            routedByCol,
            uploadedDirectly,
          );

          if(!this.isReadOnly){
            defs.push(
              {
                colId: 'actions',
                cellRenderer: DownloadXmlCellComponent,
                width: 50,
                hide: false
              }
            );
          }
        }
        else{
          defs.push(
            submitDate,
            submittedByCol,
            origSubmitDate,
            amendDateCol,
            amendReasonCol,
            publicStatus,
            ffVersionCol,
          );
       
          if(!this.isReadOnly){
            defs.push({
              colId: 'actions',
              cellRenderer: AmendCellComponent,
              width: 100,
              hide: false
            })
          }
        }

        break;
      
      case "Amending":
        defs.push(
          submitDate,
          submittedByCol,
          origSubmitDate,
          {
            field: 'lastModifiedBy',
            colId: 'lastModifiedBy',
            headerName: 'Amended By',
            filter:'agTextColumnFilter',
            editable: false,
            hide: false,
            width: 120,
            cellRenderer: (data: any) => {
              if(data?.data){
                const disclosure: Disclosure = data.data
                return disclosure.lastModifiedBy
              }
      
              return ''
            }
          },
          amendDateCol,
          amendReasonCol,
          publicStatus,
          ffVersionCol,
        );

        if(!this.isReadOnly){
          defs.push({
            colId: 'actions',
            cellRenderer: OpenRollbackCellComponent,
            width: 90,
            hide: false
          });
        }

        break;
      
      case "All":
        if(isRegisteredAgent){
          defs.push(
            {
              field: 'lastModifiedBy',
              colId: 'lastModifiedBy',
              headerName: 'Last Modified By',
              filter:'agTextColumnFilter',
              editable: false,
              hide: false,
              width: 120,
              cellRenderer: (data: any) => {
                if(data?.data){
                  const disclosure: Disclosure = data.data
                  return disclosure.lastModifiedBy
                }
        
                return ''
              }
            },
            statusCol,
          );
        }
        if(isServiceComp){
          defs.push(
            statusCol,
            routedByCol,
          );
        }
        else{
          defs.push(
            submitDate,
            submittedByCol,
            origSubmitDate,
            amendDateCol,
            amendReasonCol,
            publicStatus,
            ffVersionCol,
          );
        }

        break
      
      case "Deleted":
      case "Deleted by Operator":
        if(isServiceComp){
          defs.push(
            routedByCol,
            uploadedDirectly,
            {
              field: 'deleteDate',
              headerName: 'Delete Date',
              filter: 'agDateColumnFilter',
              filterParams: {
                buttons: ['reset'],
                dateFormat: 'mm/dd/yyyy',
                comparator: this.agGridHelperService.dateFilterComparer
              },
              editable: false,
              width: 120,
              cellRenderer: DateCellRendererComponent,
            },
          )
        }

        break;
    }
    
    if(isOperator){
      defs = defs.filter(d => d.colId !== 'operatorName');
    }

    if(isServiceComp){
      defs = defs.filter(d => d.colId !== 'serviceCompanyName');
    }

    return defs;
  }

  ngOnChanges(changes: SimpleChanges) {
    const disclosures = changes.disclosures.currentValue as Disclosure[];
    if (disclosures) {
      this.mainService.showLoading({ loading: false });
      this.setCollectionsAndTotals(disclosures)
        .then(() => {

          this.disclosuresFiltered = this.pending;
          if (this.parentComponentName === "manage") { 
            //grid height calc for Manage Disclosures
            this.gridHeight = this.agGridHelperService.calculateMinMaxGridHeight(this.disclosuresFiltered.length, 0, 11, 100);
          } else {
            //grid height calc for Dashboard
            this.gridHeight = this.agGridHelperService.calculateMinMaxGridHeight(this.disclosuresFiltered.length, 0, 3, 10);
          }

          
        this.changeDetectorRef.detectChanges(); // To update grid view
      });
    } else {
      this.mainService.showLoading({ loading: false });
    }
  }

  ascendingDateComparer(firstDate:Date, secondDate:Date){
    if (firstDate === null || firstDate === undefined) {
      return 1;
    }
    if (secondDate === null || secondDate === undefined) {
      return -1;
    }
    const dateA = new Date(firstDate).getTime();
    const dateB = new Date(secondDate).getTime();

    if (dateA < dateB) {
      return -1;
    }
    if (dateA > dateB) {
      return 1;
    }
    return 0;
  }

  descendingDateComparer(firstDate:Date, secondDate:Date){
    if (firstDate === null || firstDate === undefined) {
      return 1;
    }
    if (secondDate === null || secondDate === undefined) {
      return -1;
    }
    const dateA = new Date(firstDate).getTime();
    const dateB = new Date(secondDate).getTime();

    if (dateA > dateB) {
      return -1;
    }
    if (dateA < dateB) {
      return 1;
    }
    return 0;
  }

  ascendingStringComparer(a:string, b:string){
    if (a === null || a === undefined) {
      return 1;
    }
    if (b === null || b === undefined) {
      return -1;
    }
    return a.localeCompare(b);
  }

  descendingStringComparer(a:string, b:string){
    if (a === null || a === undefined) {
      return 1;
    }
    if (b === null || b === undefined) {
      return -1;
    }
    return b.localeCompare(a);
  }

  async setCollectionsAndTotals(disclosures: Disclosure[]) {  
    const user = await this.authService.getUserInfo();
    let userCurrentOrg = user.getCurrentOrg();
    
    if (userCurrentOrg && userCurrentOrg.isServiceComp) {
      //Disclosures not yet routed to operator
      const serviceCompDisclosures = disclosures.filter(d => d.currentOrgId === user.currentOrganizationId);

      this.pending = serviceCompDisclosures.filter(x => x.publicStatus === "Pending")?.sort((a, b) => {
        return this.descendingDateComparer(a.lastModifiedDate, b.lastModifiedDate);
      });

      this.amending = serviceCompDisclosures.filter(d_1 => d_1.publicStatus === "Amending")?.sort((a, b) => {
        return this.ascendingDateComparer(a.lastModifiedDate, b.lastModifiedDate);
        });
      
      this.deleted = serviceCompDisclosures.filter(x_1 => x_1.publicStatus === "Deleted")?.sort((a, b) => {
        return this.ascendingStringComparer(a.apiNumber, b.apiNumber);
      });

      this.submitted = disclosures.filter(d_2 => d_2.currentOrgId !== user.currentOrganizationId && d_2.publicStatus !== 'Archived' && d_2.publicStatus !== 'Deleted');
      this.deletedByOperator = disclosures.filter(d_3 => d_3.publicStatus === "Deleted" && d_3.currentOrgId !== user.primaryOrganizationId);
      this.all = disclosures.filter(x_5 => x_5.publicStatus !== "Archived");
    } else if (userCurrentOrg && userCurrentOrg.isRegisteredAgent) {

      const regAgentDisclosures = disclosures.filter(d => this.orgRelations.find(o => o.organizationId === d.operatorOrganizationId) != null);
      
      this.pending = regAgentDisclosures.filter(x_2 => x_2.publicStatus === "Pending")?.sort((a, b) => {
        return this.descendingDateComparer(a.lastModifiedDate, b.lastModifiedDate);
      });

      this.deleted = regAgentDisclosures.filter(x_2 => x_2.publicStatus === "Deleted")?.sort((a, b) => {
          return this.ascendingStringComparer(a.apiNumber, b.apiNumber);
      });

      this.amending = regAgentDisclosures.filter(d_4 => d_4.publicStatus === "Amending")?.sort((a, b) => {
        return this.descendingDateComparer(a.lastModifiedDate, b.lastModifiedDate);
      });

      this.submitted = regAgentDisclosures.filter(x_4 => x_4.publicStatus === "Submitted" && !this.amending.some(d_5 => d_5.fracJobId === x_4.fracJobId))?.sort((a, b) => {
        return this.ascendingStringComparer(a.apiNumber, b.apiNumber);
      });

    }
    else {
      this.pending = disclosures.filter(x_2 => x_2.publicStatus === "Pending")?.sort((a, b) => {
        return this.descendingDateComparer(a.lastModifiedDate, b.lastModifiedDate);
      });

      this.deleted = disclosures.filter(x_3 => x_3.publicStatus === "Deleted")?.sort((a, b) => {
        return this.ascendingStringComparer(a.apiNumber, b.apiNumber);
      });
      
      this.amending = disclosures.filter(d_4 => d_4.publicStatus === "Amending")?.sort((a, b) => {
        return this.descendingDateComparer(a.lastModifiedDate, b.lastModifiedDate);
      });

      this.submitted = disclosures.filter(x_4 => x_4.publicStatus === "Submitted" && !this.amending.some(d_5 => d_5.fracJobId === x_4.fracJobId))?.sort((a, b) => {
        return this.ascendingStringComparer(a.apiNumber, b.apiNumber);
      });

      this.all = disclosures
      .filter(x_5 => x_5.publicStatus !== "Archived")
      .filter(x_5 => {
        if(x_5.publicStatus === "Submitted"){
          const isDisclosureAmending = this.amending.some(d_5 => d_5.fracJobId === x_5.fracJobId);
          return !isDisclosureAmending;
        }
        return true;
      })
      ?.sort((a, b) => {
        return this.ascendingStringComparer(a.apiNumber, b.apiNumber);
      });
    }

    this.totalPending = this.pending.length;
    this.totalSubmitted = this.submitted.length;
    this.totalAmending = this.amending.length;
    this.totalDeleted = this.deleted.length;
    this.totalDeletedByOperator = this.deletedByOperator.length;
    this.totalAll = this.all.length;
  }

  cancelDelete() {
    this.modalVisible = false;
  }
  confirmDelete() {
    this.mainService.showLoading({ loading: true });
    this.modalVisible = false;
    this.disclosureService.deleteDisclosure(this.selectedDisclosure.disclosureId).subscribe({
      next: () => {
        this.mainService.showLoading({ loading: false });
        this.loggingService.success("Disclosure was deleted");
        const deleted = this.disclosures.find(x => x.disclosureId === this.selectedDisclosure.disclosureId);
        if (deleted) {
          deleted.status = "Deleted";
          deleted.publicStatus = "Deleted";
        }
        
        this.setCollectionsAndTotals(this.disclosures)
        .then( () => {
          this.disclosuresFiltered = this.pending;
          this.changeDetectorRef.detectChanges();
        });
      },
      error: (e) => {
        this.mainService.showLoading({ loading: false });
        this.loggingService.error("Error deleting disclosure");
      }
    });
  }

  modalClosed(e: any) {
    this.cancelDelete();
  }

  cancelAmend() {
    this.modalAmendVisible = false;
  }

  confirmAmend() {
    this.mainService.showLoading({ loading: true });
    this.modalAmendVisible = false;

    const { currentOrganizationId } = this.userInfo;

    this.disclosureService.SetMyDisclosureToAmending(currentOrganizationId, this.selectedDisclosure.disclosureId).subscribe(res => {
      this.mainService.showLoading({ loading: false });
      this.router.navigate(['/disclosure/edit-disclosure/', res.disclosureId]);
    }, err => {
      this.mainService.showLoading({ loading: false });
      this.loggingService.error("Error setting disclosure to amending");
    });
    /*this.disclosureService.deleteDisclosure(this.selectedDisclosure.disclosureId).subscribe({
      next: () => {
        this.mainService.showLoading({ loading: false });
        this.loggingService.success("Disclosure was deleted");
        const deleted = this.disclosures.find(x => x.disclosureId === this.selectedDisclosure.disclosureId);
        if (deleted) {
          deleted.status = "Deleted";
        }
        this.setCollectionsAndTotals(this.disclosures);
        this.filterForm.controls.status.patchValue("Editing");
        this.changeDetectorRef.detectChanges();
      },
      error: (e) => {
        this.mainService.showLoading({ loading: false });
        this.loggingService.error("Error deleting disclosure");
      }
    });*/
  }

  modalAmendClosed(e: any) {
    this.cancelAmend();
  }

  ngOnDestroy(): void {
  }

  confirmRollback(){
    this.modalRollbkVisible = false
    
    this.authService.getUserInfo()
    .then(user => { 
     
      this.disclosureService.rollBackMyDisclosure(user.currentOrganizationId, this.selectedDisclosure.disclosureId)
      .pipe( first() )
      .subscribe( 
        {
          next: () => {
            this.loggingService.success('Disclosure Rollback successful');
            this.selectedDisclosure.publicStatus = 'Deleted';
            const submitted = this.disclosures.filter(x => x.publicStatus === "Submitted");
            const submittedDisclosure: Disclosure = submitted.find((d: Disclosure) => d.fracJobId === this.selectedDisclosure.fracJobId);
            submittedDisclosure.editCount = 0; //So amend cell will render
            
            this.setCollectionsAndTotals(this.disclosures)
            .then( () => {
              this.disclosuresFiltered = this.amending;
              this.changeDetectorRef.detectChanges(); // To update grid view
            });
          },
          error: () => this.loggingService.error('Error rolling back disclosure')
        }
      )
    })
    .finally( () => this.mainService.showLoading({ loading: false }) )
  }

  cancelRollback(){
    this.modalRollbkVisible = false
  }

  apiValueGetter(params: ValueGetterParams<Disclosure>) {
    let formattedValue = this.agGridHelperService.formatAPINumber(params.data.apiNumber);
    return formattedValue;
  }

  viewXml(disclosure: Disclosure): void{
    this.fileService.generateDisclosureXml(this.currentOrganizationId, disclosure.disclosureId)
    .pipe( first() )
    .subscribe({
      next: xml => {
        FileSaver.saveAs(xml, (disclosure.wellName ? disclosure.wellName : "Disclosure") + '.xml');
      },
      error: e => {
        this.loggingService.error("Error getting disclosure Xml", "Error getting disclosure Xml " + e);
      }
    });
  }

  viewPdf(disclosure: Disclosure): void {
    const matched = this.all.find(x => x.disclosureId === disclosure.disclosureId);
    if (matched) {
      window.open(`/api/file/GetDisclosurePdfByName/${disclosure.disclosureId}/${encodeURIComponent(matched.pdfName)}`, "_blank");
    }
  }

  public exportToCSV()
  {
    this.agGridHelperService.exportDataToExcel("Disclosures", "sheet1", this.gridOptions, false);
  }
}
